import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { findIndex, get } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import ReferralList from "./components/ReferralList";
import ReferralDataForm from "./components/ReferralDataForm";
import LetterFileFormComponent from "./components/ReferralDataForm/components/LetterFileFormComponent";

import { STATUS } from "../../constants";
import Loading from "../../components/Shared/Loading";
import { FormMultipleSelectField } from "./components/ReferralDataForm/components/FormComponents";
import { assignReferralSpeciality } from "../../services/rawReferrals";
import {
  ALL_OPTION,
  UNASSIGNED_OPTION,
  buildSpecialityFilterOptions,
  filterReferralBySpecialities,
} from "./utils/referralFolders";

import "./index.css";
import FilesUploader from "components/FilesUploader";
import OldReferralList from "./components/OldReferralList";
import { getRawReferrals } from "./rawReferralSlice";
import { useRealtimeReferrals } from "./rawReferralAISlice";
import AiFilesUploader from "components/AiFilesUploader";

const ReferralInbox = ({ source = "ocr_clients" }) => {
  const dispatch = useDispatch();
  useRealtimeReferrals(source);
  const { status: statusOld, data: referralsOld = [] } = useSelector(({ rawReferrals }) => rawReferrals);
  const { status: statusAI, data: referralsAI = [] } = useSelector(({ rawReferralsAI }) => rawReferralsAI);
  const { specialities: SPECIALTIES = [] } = useSelector(({ app }) => app);

  const [activeReferral, setActiveReferral] = useState(null);
  const [isAI, setIsAI] = useState(source === "ai_extracted_referrals");
  const [status, setStatus] = useState(isAI ? statusAI : statusOld);
  const [referrals, setReferrals] = useState(isAI ? referralsAI : referralsOld);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSpecialities, setSelectedSpecialities] = useState([ALL_OPTION]);
  const [selectedReferrals, setSelectedReferrals] = useState([]);
  const [assigningReferralId, setAssigningReferralId] = useState(null);
  const [assignFeedback, setAssignFeedback] = useState(null);
  const assigningRef = useRef(false);

  const specialityNames = useMemo(() => SPECIALTIES.map(({ name }) => name), [SPECIALTIES]);

  // Counted over every referral, not over the filtered view.
  const folderOptions = useMemo(() => buildSpecialityFilterOptions(specialityNames, referrals), [
    specialityNames,
    referrals,
  ]);

  const handleSelectSpecialities = useCallback(
    (selectedOptions) => {
      const selectedSpecialities = selectedOptions.map((option) => option.value);
      setSelectedSpecialities(selectedSpecialities);
      setSelectedReferrals(filterReferralBySpecialities(referrals, selectedSpecialities));
    },
    [referrals]
  );

  const handleAssignReferral = useCallback(
    async (referral, specialityName) => {
      // The ref, not the state: setState is async, so a second click would slip past a
      // state-only check before the first render lands.
      if (assigningRef.current) {
        return;
      }
      assigningRef.current = true;
      setAssigningReferralId(referral.id);
      setAssignFeedback(null);
      try {
        const { error } = await assignReferralSpeciality({ id: referral.id, specialityName, source });
        if (error) {
          throw error;
        }
        // The referral may leave the current filter, so say where it went.
        setAssignFeedback({
          type: "success",
          message: `Assigned to ${specialityName || UNASSIGNED_OPTION}.`,
        });
      } catch (error) {
        console.error("[ReferralInbox] assign referral speciality failed:", error);
        setAssignFeedback({
          type: "danger",
          message: get(error, "response.data.error", "Could not assign the referral — please try again."),
        });
      } finally {
        assigningRef.current = false;
        setAssigningReferralId(null);
      }
    },
    [source]
  );

  useEffect(() => {
    setIsAI(source === "ai_extracted_referrals");
  }, [source]);
  useEffect(() => {
    setStatus(isAI ? statusAI : statusOld);
  }, [isAI, statusAI, statusOld]);
  useEffect(() => {
    setReferrals(isAI ? referralsAI : referralsOld);
  }, [isAI, referralsAI, referralsOld]);

  useEffect(() => {
    if ([STATUS.DEFAULT].includes(status)) {
      source === "ocr_clients" && dispatch(getRawReferrals(source));
    }
    if (STATUS.LOADING === status) {
      setActiveReferral(null);
      setSelectedReferrals([]);
    }
    setIsLoading(STATUS.LOADING === status);
  }, [source, dispatch, status]);

  useEffect(() => {
    setSelectedReferrals(filterReferralBySpecialities(referrals, selectedSpecialities));
  }, [referrals, selectedSpecialities]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeReferral) {
      setActiveReferral(selectedReferrals[0] || null);
    } else {
      const activeIndex = findIndex(selectedReferrals, { id: activeReferral.id });
      if (activeIndex === -1) {
        setActiveReferral(selectedReferrals[0] || null);
      }
    }
  }, [activeReferral, selectedReferrals]);

  const referral = activeReferral || selectedReferrals[0] || null;

  return isLoading ? (
    <Loading />
  ) : (
    <>
      {isAI ? (
        <AiFilesUploader type="referral" buttonTitle="Upload Referrals" />
      ) : (
        <FilesUploader folder="referral-upload-working" buttonTitle="Upload Referrals" />
      )}
      <div className="columns referral-inbox-container">
        <div className="column is-2 referral-list-container">
          <p className="menu-label">{`Speciality (${selectedReferrals.length})`}</p>
          <FormMultipleSelectField
            isMultiple={true}
            selectedValues={selectedSpecialities}
            options={folderOptions}
            onValueChange={handleSelectSpecialities}
          />
          {assignFeedback && (
            <article className={`message is-${assignFeedback.type} is-size-7`}>
              <div className="message-body is-radiusless">{assignFeedback.message}</div>
            </article>
          )}
          {isAI ? (
            <ReferralList
              referrals={selectedReferrals}
              activeReferral={activeReferral || referrals[0] || null}
              onSelectReferral={setActiveReferral}
              specialityNames={specialityNames}
              assigningReferralId={assigningReferralId}
              onAssignReferral={handleAssignReferral}
            />
          ) : (
            <OldReferralList
              referrals={selectedReferrals}
              activeReferral={activeReferral || referrals[0] || null}
              onSelectReferral={setActiveReferral}
            />
          )}
        </div>
        {selectedReferrals.length > 0 ? (
          <>
            <div className="column" style={{ padding: "0 1%" }}>
              <ReferralDataForm key={activeReferral?.id || ""} referral={referral} source={source} />
            </div>
            <div className="column is-5" style={{ overflowX: "scroll", textAlign: "center" }}>
              <p className="menu-label">FILE</p>
              <LetterFileFormComponent key={referral?.fileName} fileName={referral?.fileName} source={source} />
            </div>
          </>
        ) : (
          <div>No Data</div>
        )}
      </div>
    </>
  );
};

export default ReferralInbox;
